import { v4 as uuid } from 'uuid';
import type { MyIpReturnState } from '$/domains/myIp/returnState';

export const MY_IP_RETURN_QUERY = 'ip_return';
export const MY_IP_RESTORE_QUERY = 'ip_restore';
const storagePrefix = 'roboneo:my-ip-return:';
const maxAge = 24 * 60 * 60 * 1000;
// 创建页的资产库也可打开独立详情，返回时需保留原 IP 与 Agent 房间。
const sourcePaths = new Set(['myips/create', 'myips', 'home', 'project', 'studio', 'ai_flow', 'team_studio', 'case_detail']);
const teamStudioPathPattern = /^\/(?:--roboneo\/)?team_studio(?:[?#]|$)/;

/** 判断返回地址是否是 Team Studio 房间，用于恢复资产库和按完整 URL 回房间。 */
export function isMyIpTeamStudioReturnHref(href: string): boolean {
  return teamStudioPathPattern.test(href);
}

/** Next query 可能是字符串数组，返回来源 token 只取第一个合法值。 */
export function firstMyIpReturnQueryValue(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

/** 返回链接仅接受当前站点的已知业务入口，保留房间参数和锚点，不接受外站或详情循环。 */
export function normalizeMyIpReturnSource(value: unknown): string | undefined {
  // URL 解析会吞掉控制字符，必须在解析前拒绝，防止混淆来源检查。
  // eslint-disable-next-line no-control-regex
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u0020]/.test(value)) return undefined;
  try {
    const url = new URL(value, 'https://my-ip.invalid');
    const path = url.pathname.replace(/^\/--roboneo(?=\/)/, '');
    if (url.origin !== 'https://my-ip.invalid' || !sourcePaths.has(path.slice(1))) return undefined;
    url.searchParams.delete(MY_IP_RESTORE_QUERY);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

/** 仅保存界面状态及兜底候选来源，宿主页签独立保留；剔除详情和媒体地址。 */
export function normalizeMyIpReturnState(value: unknown): MyIpReturnState | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const state = value as MyIpReturnState;
  if (!Number.isInteger(state.page) || state.page < 1 || state.page > 100
    || !Number.isFinite(state.scrollTop) || state.scrollTop < 0 || state.scrollTop > 10000000) return undefined;
  if (state.kind === 'list') return { kind: 'list', page: state.page, scrollTop: state.scrollTop };
  if (state.kind !== 'asset' || !['teamFlowSidebar', 'teamFlowNodePanel'].includes(state.host)
    || !['personal', 'official'].includes(state.assetType)
    || (state.listAssetType !== undefined && !['personal', 'official'].includes(state.listAssetType))
    || [state.input, state.keyword, state.selectedId].some((text) => typeof text !== 'string' || text.length > 1000)
    || [state.panelScrollTop, state.detailScrollTop].some((offset) => !Number.isFinite(offset) || offset < 0 || offset > 10000000)) return undefined;
  const view = state.detailView;
  if (view !== undefined && (!view || typeof view !== 'object' || Array.isArray(view)
    || !['settings', 'materials'].includes(view.tab) || !['main', 'threeViews'].includes(view.mode)
    || !Number.isSafeInteger(view.imageIndex) || view.imageIndex < 0 || view.imageIndex > 10000)) return undefined;
  return {
    kind: 'asset',
    host: state.host,
    assetType: state.assetType,
    ...(state.listAssetType ? { listAssetType: state.listAssetType } : {}),
    input: state.input,
    keyword: state.keyword,
    selectedId: state.selectedId,
    page: state.page,
    scrollTop: state.scrollTop,
    panelScrollTop: state.panelScrollTop,
    detailScrollTop: state.detailScrollTop,
    ...(view ? { detailView: { tab: view.tab, mode: view.mode, imageIndex: view.imageIndex } } : {}),
  };
}

/** 读取统一校验记录，所有恢复消费者沿用账号、空间、来源白名单及有效期检查。 */
function readRecord(token: unknown, scopeKey: string) {
  if (typeof token !== 'string' || !/^[\da-f-]{36}$/i.test(token) || !scopeKey || typeof window === 'undefined') return undefined;
  const record = JSON.parse(window.sessionStorage.getItem(`${storagePrefix}${token}`) || 'null');
  if (!record || record.scopeKey !== scopeKey || typeof record.createdAt !== 'number'
    || record.createdAt > Date.now() || Date.now() - record.createdAt > maxAge) return undefined;
  const href = normalizeMyIpReturnSource(record.href);
  return href ? { ...record, href } : undefined;
}

/** 只在用户点击详情前更新状态，不在渲染阶段写存储，也不保存业务列表缓存。 */
export function rememberMyIpReturnState(detailHref: string, scopeKey: string, state: MyIpReturnState): void {
  try {
    const token = new URL(detailHref, 'https://my-ip.invalid').searchParams.get(MY_IP_RETURN_QUERY);
    const record = readRecord(token, scopeKey);
    const normalized = normalizeMyIpReturnState(state);
    if (!record || !normalized) return;
    window.sessionStorage.setItem(`${storagePrefix}${token}`, JSON.stringify({ ...record, state: normalized }));
  } catch {
    // 存储不可用时继续普通导航，不阻塞详情。
  }
}

/** 返回来源成功挂载后消费一次；刷新和重放 token 不能再次打开弹窗或覆盖用户操作。 */
export function consumeMyIpReturnState(token: unknown, scopeKey: string, source: string): MyIpReturnState | undefined {
  try {
    const record = readRecord(token, scopeKey);
    if (!record || record.href !== normalizeMyIpReturnSource(source)) return undefined;
    const state = normalizeMyIpReturnState(record.state);
    if (state?.kind === 'asset' && !isMyIpTeamStudioReturnHref(record.href)) return undefined;
    delete record.state;
    window.sessionStorage.setItem(`${storagePrefix}${token}`, JSON.stringify(record));
    return state;
  } catch {
    return undefined;
  }
}

/** 仅保存导航来源，不保存 IP 内容；作用域和有效期防止切换账号后复用旧入口。 */
export function rememberMyIpReturnSource(source: string, scopeKey: string): string | undefined {
  const href = normalizeMyIpReturnSource(source);
  if (!href || !scopeKey || typeof window === 'undefined') return undefined;
  try {
    const token = uuid();
    window.sessionStorage.setItem(`${storagePrefix}${token}`, JSON.stringify({ href, scopeKey, createdAt: Date.now() }));
    return token;
  } catch {
    // 禁用存储时仍可进入详情，返回采用明确的列表回退。
    return undefined;
  }
}

/** 刷新后按本标签页记录恢复来源；直访、多值参数、旧账号或损坏记录一律安全回退。 */
export function resolveMyIpReturnHref(token: unknown, scopeKey: string, fallback: string): string {
  try {
    const record = readRecord(token, scopeKey);
    if (!record) return fallback;
    if (!normalizeMyIpReturnState(record.state)) return record.href;
    const url = new URL(record.href, 'https://my-ip.invalid');
    url.searchParams.set(MY_IP_RESTORE_QUERY, String(token));
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}
